import { DocumentType, Media, UploadType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react';
import {
    customerMediaSelector,
    internalMediaSelector,
    priceMatrixMediaSelector,
    removeUploadedFile,
    supplierMediaSelector,
    updateUploadedFileTitle,
    uploadFile,
} from '@synergy/data-access';
import { getLocalizedString } from '@synergy/utils';

import { PaloAttachments } from '../../../common/components/palo/palo-attachments';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';
import { customerLabel } from '../utils/customer-label';
import { noticeUploaded } from '../../../common/utils/upload-notice';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePaloPositionMedia } from '../../../common/components/palo/palo-position-media';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export interface PaloInquiryAttachmentsProps {
    /** The supplier's side closes once the inquiry is out. */
    supplierReadOnly?: boolean;
    /** The member's side, open while the inquiry is recorded or running. */
    customerReadOnly?: boolean;
}

/** Which delivery address a sent price info belongs to: its file name ends in `_<id>-NN.pdf`. */
const addressOfPriceInfo = (media: Media, accountPurchaseIds: (number | undefined)[]): number =>
    accountPurchaseIds.findIndex(id => !!id && new RegExp(`_${id}-\\d{2}\\.pdf$`).test(media.originalName ?? ''));

/** The files of an inquiry, for the dock's "Anhänge" tab. */
export const PaloInquiryAttachments = ({
    supplierReadOnly,
    customerReadOnly,
}: PaloInquiryAttachmentsProps): ReactElement => {
    const dispatch = useThunkDispatch();
    const supplier = useAppSelector(supplierMediaSelector);
    const customer = useAppSelector(customerMediaSelector);
    const priceMatrix = useAppSelector(priceMatrixMediaSelector);
    const internal = useAppSelector(internalMediaSelector);
    const downloadBaseUrl = useAppSelector(state => state.settings.mediaBaseUrl);
    const documentId = useAppSelector(state => state.inquiries.entity?.id);
    const accountPurchases = useAppSelector(state => state.inquiries.entity?.accountPurchases);
    const locale = useAppSelector(bestLocaleSelector);

    /**
     * What the positions carry, named after the position it belongs to. The
     * rail lists everything the inquiry holds; adding and removing stays at the
     * position (key-user brief A3.3).
     */
    const positionMedia = usePaloPositionMedia();
    const positionFiles = (accountPurchases ?? []).flatMap(purchase =>
        (purchase.purchaseItems ?? []).flatMap((item, index) =>
            positionMedia(item.id, item.media).map(media =>
                Media.fromPlainObject({
                    ...media,
                    originalName: `Pos. ${index + 1} · ${getLocalizedString([locale], item.name)} — ${
                        media.originalName
                    }`,
                })
            )
        )
    );

    const customerCount = new Set((accountPurchases ?? []).map(purchase => purchase.customerAccount?.id)).size;

    /** A sent price info named by the customer it went to; the PDF's own name is long. */
    const sentPriceInfos = priceMatrix.map(media => {
        const index = addressOfPriceInfo(
            media,
            (accountPurchases ?? []).map(purchase => purchase.id)
        );
        const name = index >= 0 ? customerLabel(accountPurchases?.[index]) : undefined;

        return name && !media.title
            ? Media.fromPlainObject({ ...media, title: `Mitglied ${index + 1} · ${name}` })
            : media;
    });

    const drop =
        (existing: Media[], type: UploadType) =>
        (files: File[], titles: string[] = []) => {
            const deduplicated = files
                .map((file, index) => ({ file, title: titles[index] }))
                .filter(
                    ({ file }) => !existing.some(media => media.originalName === file.name && media.size === file.size)
                );

            if (documentId && deduplicated.length > 0) {
                void noticeUploaded(
                    dispatch,
                    deduplicated.map(({ file, title }) =>
                        dispatch(uploadFile(DocumentType.Inquiry, documentId, file, type, title))
                    )
                );
            }
        };

    const remove = (media: Media) => {
        if (documentId) {
            dispatch(removeUploadedFile(DocumentType.Inquiry, documentId, media));
        }
    };

    const rename = (media: Media, title: string) => dispatch(updateUploadedFileTitle(media, title));

    return (
        <PaloAttachments
            groups={[
                {
                    key: 'supplier',
                    label: <FormattedMessage id="app.attachments.group.supplier" />,
                    files: supplier,
                    downloadBaseUrl,
                    onDrop: supplierReadOnly || !documentId ? undefined : drop(supplier, UploadType.Supplier),
                    onRemove: supplierReadOnly ? undefined : remove,
                    onRename: supplierReadOnly ? undefined : rename,
                    onCancel: media => media.cancel?.(),
                    readOnly: supplierReadOnly,
                    hint: <FormattedMessage id="app.upload.saveBeforeDrop" />,
                },
                {
                    // Stood beside the remark on the offers tab; same files,
                    // only the place changed (#2256).
                    key: 'customer',
                    label: <FormattedMessage id="app.attachments.group.customerWithPriceInfo" />,
                    files: customer,
                    downloadBaseUrl,
                    onDrop: customerReadOnly || !documentId ? undefined : drop(customer, UploadType.Customer),
                    onRemove: customerReadOnly ? undefined : remove,
                    onRename: customerReadOnly ? undefined : rename,
                    onCancel: media => media.cancel?.(),
                    readOnly: customerReadOnly,
                    hint: <FormattedMessage id="app.upload.saveBeforeDrop" />,
                    // Kept on the inquiry, not on a customer: every price info
                    // of the inquiry links all of them.
                    note: (
                        <FormattedMessage
                            id={
                                customerCount > 1
                                    ? 'app.attachments.group.customerWithPriceInfo.toAll'
                                    : 'app.attachments.group.customerWithPriceInfo.note'
                            }
                            values={{ count: customerCount }}
                        />
                    ),
                    emphasized: true,
                },
                {
                    key: 'priceMatrix',
                    label: <FormattedMessage id="app.attachments.group.sentPriceInfos" />,
                    files: sentPriceInfos,
                    downloadBaseUrl,
                    readOnly: true,
                    hideWhenEmpty: true,
                },
                {
                    key: 'internal',
                    label: <FormattedMessage id="app.attachments.group.internal" />,
                    files: internal,
                    downloadBaseUrl,
                    onDrop: documentId ? drop(internal, UploadType.Internal) : undefined,
                    onRemove: remove,
                    onRename: rename,
                    positionFiles,
                    onCancel: media => media.cancel?.(),
                    hint: <FormattedMessage id="app.upload.saveBeforeDrop" />,
                },
            ]}
        />
    );
};
